import { KeyboardArrowDown as ArrowDownIcon, KeyboardArrowUp as ArrowUpIcon } from "@mui/icons-material";
import { Box, Button, Card, CardContent, Typography } from "@mui/material";
import { type MouseEvent, type ReactNode, useLayoutEffect, useRef, useState } from "react";

import { GenericDescription } from "~ui/atoms/GenericDescription/GenericDescription";

export interface CollapsibleDescriptionProps {
  /**
   * The description text to display
   */
  description?: string | null;
  /**
   * Optional title for the description section
   * @default "Description"
   */
  title?: string;
  /**
   * Maximum number of lines to show when collapsed
   * @default 3
   */
  maxLines?: number;
}

const CollapsibleContainer = ({ children, maxLines = 3 }: { children: ReactNode; maxLines?: number }) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
  const [showButton, setShowButton] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleClickButton = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsCollapsed(!isCollapsed);
  };

  useLayoutEffect(() => {
    if (containerRef.current) {
      const { offsetHeight, scrollHeight } = containerRef.current;
      // Add a small threshold to account for rounding errors
      setShowButton(Math.abs(offsetHeight - scrollHeight) > 1);
    }
  }, [children]);

  return (
    <Box>
      <Box
        ref={containerRef}
        sx={
          isCollapsed
            ? {
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: maxLines,
                display: "-webkit-box",
                maxWidth: "100%",
                overflow: "hidden", // Ensure text container doesn't exceed width
                wordWrap: "break-word", // Legacy support for word wrapping
              }
            : {
                maxWidth: "100%", // Ensure text container doesn't exceed width
                wordWrap: "break-word", // Legacy support for word wrapping
              }
        }
      >
        {children}
      </Box>
      {showButton && (
        <Button
          color="inherit"
          onClick={handleClickButton}
          size="small"
          startIcon={isCollapsed ? <ArrowDownIcon /> : <ArrowUpIcon />}
          sx={{ float: "right", mt: 1 }}
          variant="text"
          data-testid="toggle-description-button"
        >
          {isCollapsed ? "View More" : "View Less"}
        </Button>
      )}
    </Box>
  );
};

export const CollapsibleDescription = ({
  description,
  title = "Description",
  maxLines = 3,
}: CollapsibleDescriptionProps) => {
  // Don't render if no description
  if (!description || description.trim().length === 0) {
    return null;
  }

  return (
    <Card
      sx={{
        "&:hover": {
          boxShadow: 4,
          transform: "translateY(-2px)",
        },
        display: "flex",
        flexDirection: "column",
        height: "100%",
        maxWidth: "100%",
        mb: 0, // Ensure card doesn't exceed container width
        overflow: "hidden", // Hide any overflow content
        transition: "all 0.2s",
      }}
    >
      <CardContent sx={{ flex: 1 }}>
        <Typography variant="h6" component="h2" gutterBottom fontWeight="bold">
          📝 {title}
        </Typography>
        <CollapsibleContainer maxLines={maxLines}>
          <GenericDescription description={description} />
        </CollapsibleContainer>
      </CardContent>
    </Card>
  );
};
